import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { loginAnonymously, logoutUser } from '../../../firebase/authService';
import { checkSubmissionExists, saveSubmission } from '../../../firebase/firestoreService';

const initialState = {
  currentStep: 0,

  // Authentication State
  auth: {
    uid: null,
    isLoading: true,
    hasSubmitted: false,
    submissionData: null,
    error: null
  },

    queryParams: {}, 

  // Submission State
  isSubmitting: false,
  submitSuccess: false,
  submitError: null,

  // STEP 1: Theme
  theme: {
    themeId: '',
    themeTitle: '',
    customTheme: ''
  },

  // STEP 2: Idea Questions
  ideaAnswers: {
    problem: '',
    affectedWho: '',
    why: '',
    solution: ''
  },

  // STEP 3: Submit Details
  submitDetails: {
    name: '',
    gender: '',
    phone: '',
    district: '',
    profession: '',
    gradeGroup: '',
    isTeam: '',
    affiliation: '', 
  }
};

/* ============================================
   ASYNC THUNKS (Firebase Operations)
   ============================================ */

/**
 * Initialize authentication and check submission status
 */
export const initializeAuth = createAsyncThunk(
  'ideaSubmission/initializeAuth',
  async (_, { rejectWithValue }) => {
    try {
      // Step 1: Anonymous login
      const uid = await loginAnonymously();

      // Step 2: Check if this UID has already submitted
      const { exists, data } = await checkSubmissionExists(uid);

      return {
        uid,
        hasSubmitted: exists,
        submissionData: data
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Submit the form data to Firestore
 */
export const submitForm = createAsyncThunk(
  'ideaSubmission/submitForm',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState().ideaSubmission;
      const { uid } = state.auth;

      if (!uid) {
        throw new Error('User not authenticated');
      }

      // Prepare submission data
      const submissionData = {
        theme: state.theme,
        ideaAnswers: state.ideaAnswers,
        submitDetails: state.submitDetails,
        queryParams: state.queryParams,

      };

      // Save to Firestore
      await saveSubmission(uid, submissionData);

      return { uid, submissionData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Logout and create new session for another submission
 */
export const startNewSubmission = createAsyncThunk(
  'ideaSubmission/startNewSubmission',
  async (_, { rejectWithValue }) => {
    try {
      // Step 1: Logout current user
      await logoutUser();

      // Step 2: Login with new anonymous UID
      const newUid = await loginAnonymously();

      return { uid: newUid };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const ideaSubmissionSlice = createSlice({
  name: 'ideaSubmission',
  initialState,
  reducers: {
    /* ---------------- STEP NAVIGATION ---------------- */
    goNext(state) {
      state.currentStep = Math.min(state.currentStep + 1, 3);
    }
    ,
    goBack(state) {
      state.currentStep = Math.max(state.currentStep - 1, 0);
    },
    setStep(state, action) {
      state.currentStep = action.payload;
    },
    setQueryParams(state, action) {
  state.queryParams = action.payload;
}
,

    /* ---------------- STEP 1: THEME ---------------- */
    setTheme(state, action) {
      state.theme.themeId = action.payload.themeId;
      state.theme.themeTitle = action.payload.themeTitle || '';
      state.theme.customTheme = action.payload.customTheme || '';
    },


    /* ---------------- STEP 2: IDEA QUESTIONS ---------------- */
    setIdeaAnswers(state, action) {
      state.ideaAnswers = {
        ...state.ideaAnswers,
        ...action.payload
      };
    },

    /* ---------------- STEP 3: SUBMIT DETAILS ---------------- */
    setSubmitDetails(state, action) {
      state.submitDetails = {
        ...state.submitDetails,
        ...action.payload
      };
    },

    /* ---------------- RESET (optional, useful later) ---------------- */
    resetSubmission() {
      return initialState;
    }
  },

  /* ============================================
     EXTRA REDUCERS (Handle Async Thunks)
     ============================================ */
  extraReducers: (builder) => {
    // Initialize Auth
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.auth.isLoading = true;
        state.auth.error = null;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.auth.isLoading = false;
        state.auth.uid = action.payload.uid;
        state.auth.hasSubmitted = action.payload.hasSubmitted;
        state.auth.submissionData = action.payload.submissionData;
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.auth.isLoading = false;
        state.auth.error = action.payload;
      });

    // Submit Form
    builder
      .addCase(submitForm.pending, (state) => {
        state.isSubmitting = true;
        state.submitError = null;
        state.submitSuccess = false;
      })
      .addCase(submitForm.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.submitSuccess = true;


        state.auth.submissionData = action.payload.submissionData;
      })
      .addCase(submitForm.rejected, (state, action) => {
        state.isSubmitting = false;
        state.submitError = action.payload;
      });

    // Start New Submission
    builder
      .addCase(startNewSubmission.pending, (state) => {
        state.auth.isLoading = true;
      })
      .addCase(startNewSubmission.fulfilled, (state, action) => {
        state.auth.isLoading = false;
        state.auth.uid = action.payload.uid;
        state.auth.hasSubmitted = false;
        state.auth.submissionData = null;
        state.submitSuccess = false;
        state.currentStep = 0;
        // Reset form data
        state.theme = initialState.theme;
        state.ideaAnswers = initialState.ideaAnswers;
        state.submitDetails = initialState.submitDetails;
      })
      .addCase(startNewSubmission.rejected, (state, action) => {
        state.auth.isLoading = false;
        state.auth.error = action.payload;
      });
  }
});

export const {
  goNext,
  goBack,
  setStep,
  setTheme,
  setIdeaAnswers,
   setQueryParams,
  setSubmitDetails,
  resetSubmission
} = ideaSubmissionSlice.actions;

export default ideaSubmissionSlice.reducer;
